import type { ToolRegistryEntry } from "@/types/tools";
import { db } from "@/lib/db";

export async function loadToolRegistry(): Promise<ToolRegistryEntry[]> {
  return await db.toolRegistry.toArray();
}

export async function getEnabledTools(agentModeEnabled: boolean): Promise<ToolRegistryEntry[]> {
  if (!agentModeEnabled) return [];
  const allTools = await loadToolRegistry();
  return allTools.filter((tool) => tool.enabled);
}

export async function initializeToolRegistry(): Promise<void> {
  const existing = await db.toolRegistry.count();
  if (existing > 0) return;

  const defaultTools: ToolRegistryEntry[] = [
    {
      name: "create_task",
      description: "Create a new task in the user's task list",
      inputSchema: {
        type: "object",
        properties: {
          title: { type: "string", description: "The task title" },
          description: { type: "string", description: "Optional task description" },
          priority: { type: "string", enum: ["low", "medium", "high"], description: "Task priority" },
        },
        required: ["title"],
      },
      enabled: true,
      scope: "local",
      implementation: "create_task",
    },
    {
      name: "draft_email",
      description: "Draft an email and save it locally",
      inputSchema: {
        type: "object",
        properties: {
          to: { type: "string", description: "Recipient email address" },
          subject: { type: "string", description: "Email subject" },
          body: { type: "string", description: "Email body text" },
        },
        required: ["to", "subject", "body"],
      },
      enabled: true,
      scope: "local",
      implementation: "draft_email",
    },
    {
      name: "web_search",
      description: "Search the web for up-to-date information",
      inputSchema: {
        type: "object",
        properties: { query: { type: "string", description: "Search query" } },
        required: ["query"],
      },
      enabled: true,
      scope: "server",
      implementation: "web_search",
    },
    {
      name: "image_generation",
      description: "Generate an image from a description",
      inputSchema: {
        type: "object",
        properties: { prompt: { type: "string", description: "Image description" } },
        required: ["prompt"],
      },
      enabled: true,
      scope: "server",
      implementation: "image_generation",
    },
  ];

  await db.toolRegistry.bulkAdd(defaultTools);
}
